<template>
  <AModal
    :model-value="modelValue"
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="ldf__title">
        <i class="material-icons-round ldf__title-icon">{{ isEdit ? 'edit_note' : 'note_add' }}</i>
        {{ isEdit ? '문서 정보 수정' : '약관 문서 등록' }}
      </span>
    </template>
    <template #sub>
      {{ isEdit ? '본문이 아니라 문서 마스터 정보를 수정합니다. 본문은 버전 관리 화면에서 다룹니다.'
               : '문서를 만든 뒤 버전 관리 화면에서 본문을 등록·발행하세요.' }}
    </template>

    <div class="ldf">
      <div class="ldf__grid">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">key</i>
            문서 ID <span class="p-field__req">*</span>
          </label>
          <input
            v-model.trim="form.documentId"
            class="p-input ldf__mono"
            :class="{ 'is-invalid': isDuplicateId }"
            type="text"
            :readonly="isEdit"
            maxlength="60"
            placeholder="예: terms-of-service"
          />
          <div v-if="isDuplicateId" class="ldf__err">이미 등록된 문서 ID 입니다.</div>
          <div v-else class="p-field__hint">
            <i>{{ isEdit ? 'lock' : 'info' }}</i>
            {{ isEdit
              ? '동의 기록에 그대로 남는 값이라 등록 후에는 바꿀 수 없습니다.'
              : '영문 소문자와 하이픈을 쓰세요. 등록 후에는 변경할 수 없습니다.' }}
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">category</i>
            문서 유형 <span class="p-field__req">*</span>
          </label>
          <select v-model="form.docType" class="p-input">
            <option v-for="t in DOC_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">group</i>
            노출 대상
          </label>
          <select v-model="form.targetUserType" class="p-input">
            <option v-for="t in TARGET_USER_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">sort</i>
            노출 순서
          </label>
          <input v-model.number="form.sortOrder" class="p-input" type="number" min="0" placeholder="0" />
          <div class="p-field__hint"><i>info</i>숫자가 작을수록 앱 동의 화면 위쪽에 나옵니다.</div>
        </div>
      </div>

      <!-- 언어별 표시명: 앱 가입 화면의 체크박스 라벨로 그대로 쓰입니다 -->
      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">translate</i>
          표시명 (언어별) <span class="p-field__req">*</span>
        </label>
        <div class="ldf__names">
          <div v-for="lang in LEGAL_LANGS" :key="lang.code" class="ldf__name">
            <span class="ldf__name-label">
              {{ lang.label }}
              <b v-if="lang.code === 'ko'" class="p-field__req">*</b>
            </span>
            <input
              v-model.trim="form.names[lang.code]"
              class="p-input"
              type="text"
              maxlength="100"
              :placeholder="lang.code === 'ko' ? '예: 이용약관' : '(선택)'"
            />
          </div>
        </div>
        <div class="p-field__hint"><i>info</i>앱 가입 화면의 동의 체크박스 문구로 그대로 노출됩니다.</div>
      </div>

      <div class="ldf__switch">
        <div>
          <div class="ldf__switch-label">
            <i class="material-icons-round">rule</i>
            필수 동의 문서
          </div>
          <div class="ldf__switch-sub">
            켜면 사용자가 반드시 동의해야 가입할 수 있고, 발행 시 한국어 본문이 필수가 됩니다.
          </div>
        </div>
        <button
          type="button"
          :class="['p-switch', { 'is-on': form.isRequired }]"
          @click="form.isRequired = !form.isRequired"
        ></button>
      </div>

      <div v-if="!isEdit" class="ldf__notice">
        <i class="material-icons-round">info</i>
        <span>
          새 문서는 항상 <b>미노출(activeYn=N)</b> 상태로 만들어집니다.
          본문을 발행한 뒤 목록에서 노출을 켜야 앱에 보이고 동의가 수집됩니다.
        </span>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">취소</ABtn>
      <ABtn kind="primary" icon="save" :disabled="!canSave" :loading="saving" @click="save">
        {{ isEdit ? '수정 저장' : '등록' }}
      </ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { LEGAL_LANGS, DOC_TYPES, TARGET_USER_TYPES } from "@/utils/legal";

const blankNames = () =>
  LEGAL_LANGS.reduce((acc, l) => {
    acc[l.code] = "";
    return acc;
  }, {});

export default {
  name: "LegalDocumentFormModal",
  components: { AModal, ABtn },
  props: {
    modelValue: { type: Boolean, default: false },
    // 값이 있으면 수정, 없으면 신규 등록
    doc: { type: Object, default: null },
    // 문서 ID 중복을 서버 409 전에 걸러내기 위한 기존 목록
    documents: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      LEGAL_LANGS,
      DOC_TYPES,
      TARGET_USER_TYPES,
      saving: false,
      form: {
        documentId: "",
        names: blankNames(),
        docType: "terms",
        isRequired: true,
        targetUserType: "all",
        sortOrder: 0,
      },
      original: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.doc;
    },
    isDuplicateId() {
      if (this.isEdit || !this.form.documentId) return false;
      return this.documents.some((d) => d.documentId === this.form.documentId);
    },
    canSave() {
      if (this.saving) return false;
      if (!this.form.documentId || this.isDuplicateId) return false;
      return !!this.form.names.ko;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.init();
    },
  },
  methods: {
    init() {
      this.saving = false;
      const d = this.doc;
      const names = blankNames();
      Object.keys(d?.names || {}).forEach((k) => {
        names[k] = d.names[k] || "";
      });
      this.form = {
        documentId: d?.documentId || "",
        names,
        docType: d?.docType || "terms",
        isRequired: d ? !!d.isRequired : true,
        targetUserType: d?.targetUserType || "all",
        sortOrder: d?.sortOrder ?? 0,
      };
      this.original = JSON.parse(JSON.stringify(this.form));
    },
    // 빈 언어는 보내지 않습니다.
    compactNames() {
      const out = {};
      Object.keys(this.form.names).forEach((k) => {
        if (this.form.names[k]) out[k] = this.form.names[k];
      });
      return out;
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      const names = this.compactNames();

      try {
        if (this.isEdit) {
          // PUT 은 변경할 필드만 받습니다. documentId 는 변경 불가라 제외합니다.
          const data = {};
          if (JSON.stringify(names) !== JSON.stringify(this.compactOriginalNames())) data.names = names;
          if (this.form.docType !== this.original.docType) data.docType = this.form.docType;
          if (this.form.isRequired !== this.original.isRequired) data.isRequired = this.form.isRequired;
          if (this.form.targetUserType !== this.original.targetUserType) {
            data.targetUserType = this.form.targetUserType;
          }
          if (Number(this.form.sortOrder) !== Number(this.original.sortOrder)) {
            data.sortOrder = Number(this.form.sortOrder) || 0;
          }

          if (!Object.keys(data).length) {
            this.$notifyVue({ type: "info", title: "변경된 내용이 없습니다." });
            this.saving = false;
            return;
          }

          await this.$axios({
            method: "put",
            url: `/emp/legal/documents/${encodeURIComponent(this.form.documentId)}`,
            data,
            loadingBar: true,
          });
          this.$notifyVue({ type: "success", title: "문서 정보를 수정했습니다." });
        } else {
          await this.$axios({
            method: "post",
            url: "/emp/legal/documents",
            data: {
              documentId: this.form.documentId,
              names,
              docType: this.form.docType,
              isRequired: this.form.isRequired,
              targetUserType: this.form.targetUserType,
              sortOrder: Number(this.form.sortOrder) || 0,
            },
            loadingBar: true,
          });
          this.$notifyVue({
            type: "success",
            title: "문서를 등록했습니다.",
            text: "버전 관리 화면에서 본문을 등록·발행하세요.",
          });
        }

        this.$emit("update:modelValue", false);
        this.$emit("saved", this.form.documentId);
      } catch (e) {
        console.error("문서 저장 실패:", e);
        swal.fire("저장 실패", apiErrorMessage(e, "문서를 저장하지 못했습니다."), "error");
      } finally {
        this.saving = false;
      }
    },
    compactOriginalNames() {
      const out = {};
      Object.keys(this.original.names).forEach((k) => {
        if (this.original.names[k]) out[k] = this.original.names[k];
      });
      return out;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ldf {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__mono {
    font-family: $p-font-mono;
  }

  &__err {
    margin-top: 5px;
    font-size: 11px;
    font-weight: 700;
    color: $p-err;
  }

  &__names {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__name {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__name-label {
    font-size: 11px;
    font-weight: 700;
    color: $p-ink-soft;
  }

  &__switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 13px 15px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__switch-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;

    > i {
      font-size: 16px !important;
      color: $p-point-deep;
    }
  }

  &__switch-sub {
    margin-top: 3px;
    font-size: 11px;
    line-height: 1.5;
    color: $p-ink-soft;
  }

  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 11px 13px;
    background: $p-info-soft;
    border: 1px solid rgba($p-info, 0.28);
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.55;
    color: $p-ink;

    > i { font-size: 18px !important; color: $p-info; flex-shrink: 0; }
  }
}
</style>
